import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from '@mui/lab';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { IndividualService } from '../../../Component/Api/Individual/IndividualService';
import AppAccordion from '../../../Component/Common/Components/Accordion/AppAccordion';
import AppCheckbox from '../../../Component/Common/Components/FormValidationInputs/AppCheckbox';
import { AppDatepicker } from '../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppSelectList from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { genderList, provinceList, raceList } from '../../../Component/Common/Constants/Lists/DropdownListOptions';
import { EE123PeopleRole_Edit, PeopleEditRole } from '../../../Component/Common/Constants/UserRoles';
import { SkeletonLoading } from '../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { Individual } from '../../../Component/Model/Dto/Individual';
import { userRolesState } from '../../../State/user.Atom';
import { CustomDialogTitle } from '../../Common/Custom/CustomDialogTitle';
import { EmploymentDataTable } from '../../Common/Individual/EmploymentData/EmploymentDataTable';
import { IndividualCentralDataValidationSchema } from './IndividualCentralDataValidation';
import { getDateWithoutTime } from "../../../Helpers/DateFormatter";
import { UserActivityService } from "../../../Component/Api/UserActivity/UserActivityService";
import { CDMSummariesPeopleSection } from "../../../Component/Common/Constants/UserActivityTrackingSections";
import { UserActivityActionType } from "../../../Component/Model/Enum/BEE123Enums";

interface IProps {
  id: number;
  scorecardId: string;
  showIndividualModal: boolean;
  closeIndividualModal: () => void;
  isEditable: boolean;
}

export const IndividualCentralDataModal = (props: IProps) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { control, reset, handleSubmit, formState: { isSubmitting }, watch, trigger } = useForm({
    mode: 'all',
    resolver: yupResolver(IndividualCentralDataValidationSchema)
  });

  const identificationNumber = watch('identificationNumber');
  const employeeCode = watch('employeeCode');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getEmployeeData = async (id: number) => {
    if (id === undefined || id === 0) return;
    try {
      const request: Individual.GetRequest = {
        id: id,
        scorecardId: props.scorecardId,
        LinkedType: undefined,
        InterventionId: undefined,
        filterVAStartDate: undefined,
        filterVAEndDate: undefined
      }
      setIsLoading(true);
      const result = await IndividualService.GetIndividualByID(request);
      reset(result.data as Individual.IndividualDto);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleDataSubmit = async (data: FieldValues) => {
    setIsSaving(true);
    try {
      const finalData = data as Individual.IndividualUpdateDto;
      finalData.SampledIndividualViewModel = null;
      await IndividualService.SaveIndividual(finalData);
      UserActivityService.createActivityLog({
        section: CDMSummariesPeopleSection,
        actionType: UserActivityActionType.Update,
        actionDescription: "Edit employee details",
        requestUrl: window.location.href
      });
      toast.success(`Employee updated successfully`);
      props.closeIndividualModal();
    } 
    catch (error: any) {
      console.log(error);
      error.forEach((errorMessage: string) => {
        toast.error(errorMessage);
      });
    } 
    finally {
      setIsSaving(false);
    }
  }

  useEffect(() => {
    if (identificationNumber) {
      trigger('employeeCode');
    } else if (employeeCode) {
      trigger('identificationNumber');
    }
  }, [identificationNumber, employeeCode, trigger]);

  useEffect(() => {
    getEmployeeData(props.id);
  }, [props.showIndividualModal]);

  return (
    <Dialog disableEnforceFocus open={props.showIndividualModal} onClose={() => { props.closeIndividualModal() }} aria-labelledby="form-dialog-title" maxWidth='lg'>
      <CustomDialogTitle id="individual-dialog-title" onClose={() => props.closeIndividualModal()}>
        {props.isEditable ? 'View/Edit Employee Details' : 'View Employee Details'}
      </CustomDialogTitle>
      <DialogContent>
        {isLoading && <SkeletonLoading formLoading FieldAmount={6} />}
        {!isLoading &&
          <Box>
            <AppAccordion expandOnDefault={true} label={'Employee Data'}>
              <form id='IndividualForm' onSubmit={handleSubmit(handleDataSubmit)}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={12} mt={1}>
                    <AppTextInput control={control} name="identificationNumber" label="ID Number" disabled={!props.isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="employeeCode" label="Employee Code" disabled={!props.isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="firstName" label="Name" disabled={!props.isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="lastName" label="Surname" disabled={!props.isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="gender" label="Gender" items={genderList} disabled={!props.isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="raceId" label="Race" items={raceList} disabled={!props.isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="province" label="Province" items={provinceList} disabled={!props.isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppDatepicker control={control} name="dateOfBirth" label="Date Of Birth" inputFormat='yyyy/MM/dd' disabled={!props.isEditable} maxDate={getDateWithoutTime(new Date())} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppCheckbox control={control} name='disabled' label='Disabled' disabled={!props.isEditable} />
                  </Grid>
                </Grid>
              </form>
            </AppAccordion>
            <AppAccordion expandOnDefault={false} label='Employment Data'>
              <EmploymentDataTable scorecardId={props.scorecardId} individualId={props.id} section={CDMSummariesPeopleSection} />
            </AppAccordion>
          </Box >
        }
      </DialogContent>
      <DialogActions>
        {props.isEditable &&
          <LoadingButton loading={isSubmitting} variant='contained' form='IndividualForm' type="submit" color='primary' disabled={isSaving || !props.isEditable}>
            Save
          </LoadingButton>
        }
      </DialogActions>
    </Dialog>
  )
}

